import { formatCurrency } from '@eriko/shared-types';
import { PieChart } from 'lucide-react';
import { formatPeriodLabel } from './utils';

interface ExpenseGroup {
  name: string;
  count: number;
  total: number;
}

interface ExpenseBreakdownData {
  type: string;
  period: string;
  currency?: string;
  groupBy: string;
  groups: ExpenseGroup[];
  grandTotal: number;
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function ExpenseBreakdownReport({ data }: { data: Record<string, unknown> }) {
  const breakdown = data as unknown as ExpenseBreakdownData;
  const grandTotal = Number(breakdown.grandTotal ?? 0);
  const currency = breakdown.currency ?? 'USD'; // home currency for correct symbol (CUR-1)
  const groups = [...(breakdown.groups ?? [])].sort((a, b) => b.total - a.total);
  const periodLabel = formatPeriodLabel(breakdown.period);
  const pillLabel = breakdown.groupBy
    ? `${periodLabel} · by ${breakdown.groupBy}`
    : periodLabel;

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-surface-100 px-4.5 py-3.5">
        <div className="flex items-center gap-2.5">
          <PieChart className="h-4.25 w-4.25 text-neg" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-surface-900">
            Expense Breakdown
          </span>
        </div>
        {breakdown.period && (
          <span className="rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
            {pillLabel}
          </span>
        )}
      </div>

      {/* Rows */}
      {groups.length === 0 ? (
        <div className="px-4.5 py-10 text-center text-[13px] text-surface-500">
          No expenses in this period.
        </div>
      ) : (
        <div className="px-4.5 py-2">
          {groups.map((group) => {
            const pct = grandTotal > 0 ? (group.total / grandTotal) * 100 : 0;
            const barWidth = Math.max(Math.min(pct, 100), pct > 0 ? 1.5 : 0);
            const tier: 'lead' | 'major' | 'minor' =
              pct >= 10 ? 'lead' : pct >= 5 ? 'major' : 'minor';
            const barColor =
              tier === 'lead'
                ? 'var(--color-neg)'
                : tier === 'major'
                  ? 'color-mix(in oklch, var(--color-neg) 60%, transparent)'
                  : 'color-mix(in oklch, var(--color-neg) 28%, transparent)';
            return (
              <div key={group.name} className="group py-2">
                <div className="mb-1.5 flex items-baseline justify-between gap-3">
                  <div className="flex min-w-0 items-baseline gap-2">
                    <span className="truncate text-[13px] text-surface-800">{group.name}</span>
                    <span className="shrink-0 text-[12px] text-surface-400 tabular-nums">
                      {group.count} {group.count === 1 ? 'txn' : 'txns'}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-baseline gap-2">
                    <span className="font-mono text-[13px] font-medium tabular-nums text-surface-900">
                      {formatCurrency(group.total, 2, currency)}
                    </span>
                    <span
                      className={`w-12 text-right font-mono text-[12px] tabular-nums ${
                        tier === 'lead'
                          ? 'text-neg'
                          : tier === 'major'
                            ? 'text-surface-500'
                            : 'text-surface-400'
                      }`}
                    >
                      {formatPercent(pct)}
                    </span>
                  </div>
                </div>
                <div className="h-1 overflow-hidden rounded-full bg-neg-surface">
                  <div
                    className="h-full rounded-full transition-[width] duration-500 ease-out"
                    style={{ width: `${barWidth}%`, backgroundColor: barColor }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Grand Total footer */}
      <div className="flex items-center border-t border-surface-200 px-4.5 py-2.5 text-sm font-semibold">
        <span className="flex-1 text-surface-900">Total</span>
        <span className="font-mono text-sm font-semibold tabular-nums text-surface-900">
          {formatCurrency(grandTotal, 2, currency)}
        </span>
      </div>
    </div>
  );
}
